import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const PlayerContainer = styled(motion.div)`
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  padding: 8px;
  border-radius: 50px;
  box-shadow: 0 5px 25px rgba(255, 20, 147, 0.15);
  z-index: 1000;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  max-width: ${props => props.expanded ? '300px' : 'auto'};
  
  &:hover {
    box-shadow: 0 8px 30px rgba(255, 20, 147, 0.25);
  }
  
  @media (max-width: 768px) {
    bottom: 15px;
    right: 15px;
    transform: scale(0.9);
    transform-origin: bottom right;
  }
  
  @media (max-width: 480px) {
    transform: scale(0.85);
    bottom: 10px;
    right: 10px;
  }
  
  &.minimized {
    width: auto;
    padding: 6px;
  }
`;

const PlayerButton = styled(motion.button)`
  background: linear-gradient(45deg, #ff1493, #ff69b4);
  color: white;
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(255, 20, 147, 0.3);
  font-size: 1.1rem;
  margin-right: ${props => props.marginRight || '0'};
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 15px rgba(255, 20, 147, 0.5);
    background: linear-gradient(45deg, #ff007f, #ff4da6);
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }
`;

const PlayerInfo = styled.div`
  margin-left: 15px;
  font-family: 'Vazirmatn', sans-serif;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 480px) {
    margin-left: 10px;
  }
`;

const SongTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #d84079;
  margin-bottom: 2px;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const SongArtist = styled.div`
  font-size: 0.8rem;
  color: #777;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  margin-left: 15px;
  
  @media (max-width: 480px) {
    margin-left: 10px;
  }
`;

const VolumeSlider = styled.input`
  -webkit-appearance: none;
  width: 70px;
  height: 4px;
  border-radius: 4px;
  background: linear-gradient(to right, #ff4081 0%, #ff4081 ${props => props.value * 100}%, #ffcdd2 ${props => props.value * 100}%, #ffcdd2 100%);
  outline: none;
  transition: all 0.2s;
  margin-left: 8px;
  
  &:hover {
    height: 5px;
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #ff4081;
    cursor: pointer;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
    transition: all 0.2s;
  }
  
  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
  }
  
  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #ff4081;
    cursor: pointer;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 480px) {
    width: 50px;
  }
`;

// New styled components for enhanced player
const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #ffcdd2;
  border-radius: 4px;
  margin-top: 5px;
  position: relative;
  cursor: pointer;
`;

const Progress = styled.div`
  height: 100%;
  background: linear-gradient(to right, #ff1493, #ff69b4);
  border-radius: 4px;
  width: ${props => props.width || '0%'};
  transition: width 0.1s linear;
`;

const ProgressHandle = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ff4081;
  position: absolute;
  top: 50%;
  left: ${props => props.position || '0%'};
  transform: translate(-50%, -50%);
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  transition: left 0.1s linear;
`;

const SongControls = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-top: 5px;
  gap: 10px;
`;

const SmallButton = styled(motion.button)`
  background: none;
  color: #d84079;
  border: none;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
  }
`;

const ExpandedInfo = styled(motion.div)`
  padding: 5px 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TimeInfo = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: 0.7rem;
  color: #777;
  margin-top: 2px;
`;

const MusicPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isLoaded, setIsLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [songIndex, setSongIndex] = useState(0);
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  
  // Multiple songs playlist
  const playlist = [
    {
      url: "https://assets.codepen.io/4358584/Anitek_-_Komorebi.mp3",
      title: "ملودی عاشقانه",
      artist: "برای دلآرام عزیزم"
    },
    {
      url: "https://assets.codepen.io/4358584/Moonlight.mp3",
      title: "آهنگ آرامش‌بخش",
      artist: "تقدیم به دلآرام"
    },
    {
      url: "https://www.bensound.com/bensound-music/bensound-memories.mp3",
      title: "نوای پیانو",
      artist: "برای عشق زیبایم"
    }
  ];
  
  useEffect(() => {
    // Create audio element
    audioRef.current = new Audio(playlist[songIndex].url);
    audioRef.current.loop = false;
    audioRef.current.volume = volume;
    
    // Add event listeners
    audioRef.current.addEventListener('timeupdate', updateProgress);
    audioRef.current.addEventListener('loadedmetadata', () => {
      setDuration(audioRef.current.duration);
      setIsLoaded(true);
    });
    audioRef.current.addEventListener('ended', handleSongEnd);
    
    // Try to play automatically but handle browser policies
    const playPromise = audioRef.current.play();
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsPlaying(true);
        })
        .catch(error => {
          console.warn("Autoplay was prevented by browser:", error);
          setIsPlaying(false);
          setIsLoaded(true);
        });
    }
    
    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', updateProgress);
        audioRef.current.removeEventListener('loadedmetadata', () => {});
        audioRef.current.removeEventListener('ended', handleSongEnd);
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [songIndex]);
  
  const updateProgress = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  
  const handleSongEnd = () => {
    nextSong();
  };
  
  const nextSong = () => {
    setSongIndex((prevIndex) => (prevIndex + 1) % playlist.length);
  };
  
  const prevSong = () => {
    setSongIndex((prevIndex) => (prevIndex - 1 + playlist.length) % playlist.length);
  };
  
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);
  
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(e => console.error("Error playing audio:", e));
        }
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };
  
  const handleProgressClick = (e) => {
    if (!progressRef.current || !audioRef.current) return;
    
    const progressRect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - progressRect.left) / progressRect.width;
    audioRef.current.currentTime = percent * duration;
  };
  
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  return (
    <PlayerContainer
      initial={{ y: 100, opacity: 0 }}
      animate={{ 
        y: 0, 
        opacity: 1, 
        padding: expanded ? '8px 12px' : isPlaying ? '6px 10px' : '6px',
        width: expanded ? 'auto' : 'auto' 
      }}
      transition={{ delay: 0.5, duration: 0.3, ease: "easeOut" }}
      layout
      expanded={expanded}
      className={!expanded && !isPlaying ? "minimized" : ""}
    >
      <PlayerButton 
        onClick={togglePlay}
        whileTap={{ scale: 0.9 }}
        marginRight={expanded || isPlaying ? "8px" : "0"}
        disabled={!isLoaded}
        animate={{ 
          scale: isPlaying ? [1, 1.05, 1] : 1,
          transition: { repeat: isPlaying ? Infinity : 0, duration: 2 }
        }}
      >
        {isPlaying ? "⏸️" : "▶️"}
      </PlayerButton>
      
      {(expanded || isPlaying) && (
        <div style={{ flex: 1, minWidth: expanded ? '200px' : '120px', maxWidth: expanded ? '250px' : '150px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <PlayerInfo>
              <SongTitle>{playlist[songIndex].title}</SongTitle>
              <SongArtist>{playlist[songIndex].artist}</SongArtist>
              
              {expanded && (
                <ExpandedInfo
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <ProgressBar 
                    ref={progressRef}
                    onClick={handleProgressClick}
                  >
                    <Progress width={`${(currentTime / duration) * 100}%`} />
                    <ProgressHandle position={`${(currentTime / duration) * 100}%`} />
                  </ProgressBar>
                  
                  <TimeInfo>
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </TimeInfo>
                  
                  <SongControls
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <SmallButton onClick={prevSong}>⏮️</SmallButton>
                    <SmallButton onClick={() => {
                      if (audioRef.current) {
                        audioRef.current.currentTime = 0;
                      }
                    }}>🔄</SmallButton>
                    <SmallButton onClick={nextSong}>⏭️</SmallButton>
                  </SongControls>
                </ExpandedInfo>
              )}
            </PlayerInfo>
            
            <motion.div
              style={{ cursor: 'pointer', marginLeft: '5px', fontSize: '0.9rem' }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? '▼' : '▲'}
            </motion.div>
          </div>
        </div>
      )}
      
      {expanded && (
        <VolumeControl>
          <span style={{ fontSize: "0.9rem", color: "#777" }}>
            {volume === 0 ? "🔈" : volume < 0.5 ? "🔉" : "🔊"}
          </span>
          <VolumeSlider 
            type="range" 
            min="0" 
            max="1" 
            step="0.05" 
            value={volume} 
            onChange={handleVolumeChange} 
          />
        </VolumeControl>
      )}
    </PlayerContainer>
  );
};

export default MusicPlayer; 